import { Box, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { enqueueSnackbar } from 'notistack';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { DatePicker, Loading, Select, TextField } from '../../../lib/components';
import { ArrowForwardIconSvg, EmployeesGraphicIconSvg, SaveIcon } from '../../../lib/icons';
import { useStepper } from '../partials/actionStepperPartial';
import { useStore } from '../../../hooks/useStore';
import { KeyofNamespace } from '../../../translations/keyofNs';
import { Button } from '../../../lib/components/atomic/button';

const FORM_FIELD_STYLES = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  gap: '1.5rem 2rem'
};

const BACK_TO_OVERVIEW = '/employees';

const EMPLOYEE_NUMBER_ALREADY_TAKEN = 'employee_number_already_taken';

export const CreateNewEmployee: FC = () => {
  const {
    activeStep,
    setActiveStep,
    setTitle,
    setSectionTitle,
    setBackToPath,
    setBackToTitle,
    setStepperConfig,
    stepperConfig,
    setNextButtonConfig
  } = useStepper();
  const { employeeStore } = useStore();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const btn = useRef<HTMLInputElement>(null);
  const submittedRef = useRef<boolean>(false);
  const [newEmployeeId, setNewEmployeeId] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);

  const genderOptions = [
    { text: t('common:MALE'), value: 'male' },
    { text: t('common:FEMALE'), value: 'female' },
    { text: t('common:DIVERSE'), value: 'diverse' },
    { text: t('common:UNDEFINED'), value: 'undefined' }
  ];

  const marriageStatusOptions = [
    { text: t('employee:MARRIAGE_STATUS_MARRIED'), value: 'verheiratet' },
    { text: t('employee:MARRIAGE_STATUS_SINGLE'), value: 'ledig' }
  ];

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      gender: '',
      birth_date: '',
      marriage_status: '',
      children_count: undefined,
      email: '',
      personal_number: undefined
    },
    validationSchema: Yup.object().shape({
      first_name: Yup.string().required(t('form:FIELD_IS_REQUIRED')),
      last_name: Yup.string().required(t('form:FIELD_IS_REQUIRED')),
      gender: Yup.string().oneOf(genderOptions.map((g) => g.value)),
      birth_date: Yup.string(),
      marriage_status: Yup.string().oneOf(marriageStatusOptions.map((m) => m.value)),
      children_count: Yup.number()
        .typeError(t('form:INVALID_VALUE'))
        .integer(t('form:INVALID_VALUE'))
        .min(0, t('form:INVALID_VALUE')),
      email: Yup.string().test(
        'is-not-used',
        'email is already in use.',
        async (value, context) => {
          if (!value) {
            return true;
          }
          try {
            await Yup.object()
              .shape({
                email: Yup.string().email(t('form:INVALID_EMAIL'))
              })
              .validate({ email: value });
            await employeeStore.checkNewEmailAdress(value);
          } catch (error) {
            if (error instanceof Yup.ValidationError) {
              return context.createError({
                message: t(error.message as KeyofNamespace<'form'>),
                path: 'email'
              });
            }
          }
          return true;
        }
      ),
      personal_number: Yup.string().required(t('form:FIELD_IS_REQUIRED'))
    }),
    onSubmit: async (values, { setErrors }) => {
      const isFinalStep = !!stepperConfig && !!activeStep && activeStep > stepperConfig.length;
      if (!isFinalStep || submittedRef.current) return;
      submittedRef.current = true;

      try {
        setLoading(true);
        const res = await employeeStore.createNewEmployee(values);
        setNewEmployeeId(res);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
        submittedRef.current = false;
        enqueueSnackbar(t('employee:EMPLOYEE_CREATION_FAILED'), { variant: 'error' });
        setActiveStep(1);

        const status = (error as { status?: number })?.status;
        if (status !== 400) return;
        const body = (error as { body?: unknown })?.body;
        const fieldErrors: Record<string, string> = {};
        const flatten = (prefix: string, value: unknown) => {
          if (Array.isArray(value)) {
            fieldErrors[prefix] = String(value[0]);
          } else if (typeof value === 'string') {
            fieldErrors[prefix] = value;
          } else if (value && typeof value === 'object') {
            for (const [k, v] of Object.entries(value)) {
              flatten(prefix ? `${prefix}.${k}` : k, v);
            }
          }
        };
        const payload =
          body && typeof body === 'object' && 'errors' in body
            ? (body as { errors: unknown }).errors
            : body;
        if (payload && typeof payload === 'object') flatten('', payload);

        const mapped: Record<string, string> = {};
        if (fieldErrors['user.email'] || fieldErrors['email'])
          mapped.email = fieldErrors['user.email'] ?? fieldErrors['email'];
        const employeeNumberError = fieldErrors['position_data.employee_number'];
        if (employeeNumberError)
          mapped.personal_number =
            employeeNumberError === EMPLOYEE_NUMBER_ALREADY_TAKEN
              ? t('form:EMPLOYEE_NUMBER_ALREADY_TAKEN')
              : employeeNumberError;

        if (Object.keys(mapped).length > 0) {
          setErrors(mapped);
          return;
        }
        setErrors({ email: t('form:EMAIL_ALREADY_TAKEN') });
      }
    }
  });

  useEffect(() => {
    setTitle(t('account:CREATE_EMPLOYEE'));
    setSectionTitle(t('common:PERSONAL_DATA'));
    setBackToPath(BACK_TO_OVERVIEW);
    setBackToTitle(t('account:BACK_TO_OVERVIEW'));
    setStepperConfig([
      {
        title: t('employee:CREATE_ACCOUNT')
      },
      {
        title: t('common:PERSONAL_DATA')
      }
    ]);
  }, []);

  useEffect(() => {
    if (stepperConfig && activeStep && activeStep == stepperConfig.length) {
      formik.validateForm().then((res) => {
        if (Object.keys(res).length === 0) return;
        formik.setFieldTouched('first_name');
        formik.setFieldTouched('last_name');
        formik.setFieldTouched('email');
        navigate(-1);
      });
    } else if (stepperConfig && activeStep && activeStep > stepperConfig.length) {
      formik.validateForm().then((res) => {
        if (Object.keys(res).length === 0) {
          setSectionTitle(undefined);
          btn.current?.click();
        } else {
          formik.setFieldTouched('email');
          navigate(-1);
        }
      });
    }

    if (activeStep) {
      setSectionTitle(
        stepperConfig && stepperConfig[activeStep - 1] && stepperConfig[activeStep - 1].title
      );
    }

    if (activeStep && activeStep === 2) {
      setNextButtonConfig({
        text: t('common:CREATE'),
        startIcon: <SaveIcon />,
        endIcon: undefined
      });
    } else {
      setNextButtonConfig({
        text: t('common:NEXT'),
        startIcon: undefined,
        endIcon: <ArrowForwardIconSvg />
      });
    }
  }, [activeStep, stepperConfig]);

  return (
    <Box component="form" onSubmit={formik.handleSubmit} height="100%">
      {activeStep && activeStep > 1 && activeStep < 3 && (
        <Typography paragraph variant="subtitle2" fontSize="1rem">
          {t('common:OPTINAL_DATA')}
        </Typography>
      )}
      {activeStep === 1 && (
        <Box sx={FORM_FIELD_STYLES}>
          <TextField
            label={t('employee:FIRST_NAME')}
            placeholder={t('employee:FIRST_NAME')}
            id="first_name"
            name="first_name"
            value={formik.values.first_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={!!formik.errors.first_name && formik.touched.first_name}
            helperText={formik.errors.first_name}
            required
          />
          <TextField
            label={t('employee:LAST_NAME')}
            placeholder={t('employee:LAST_NAME')}
            id="last_name"
            name="last_name"
            value={formik.values.last_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={!!formik.errors.last_name && formik.touched.last_name}
            helperText={formik.errors.last_name}
            required
          />
          <TextField
            label={t('account:EMAIL_FIELD')}
            placeholder={t('account:EMAIL_FIELD')}
            id="email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={!!formik.errors.email && formik.touched.email}
            helperText={formik.errors.email ?? t('employee:EMAIL_OPTIONAL_PDF_HINT')}
          />
          <TextField
            label={t('employee:PERSONAL_NUMBER')}
            placeholder={t('employee:PERSONAL_NUMBER')}
            id="personal_number"
            name="personal_number"
            value={formik.values.personal_number}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={!!formik.errors.personal_number && formik.touched.personal_number}
            helperText={formik.errors.personal_number}
            required
          />
        </Box>
      )}
      {activeStep === 2 && (
        <Box sx={FORM_FIELD_STYLES}>
          <DatePicker
            disableFuture
            label={t('employee:BIRTHDAY')}
            onChange={(value) => {
              formik.setFieldTouched('birth_date');
              formik.setFieldValue('birth_date', value);
            }}
            onChangeInvalidDate={(invalid) => {
              console.error(invalid);
              formik.setFieldError('birth_date', `${invalid} is invalid`);
            }}
            error={!!formik.errors.birth_date && formik.touched.birth_date}
            helperText={formik.errors.birth_date}
            initialValue={formik.values.birth_date}
            maxDate={new Date()}
            yearRange={100}
          />
          <Select
            label={t('employee:GENDER')}
            placeholder={t('employee:GENDER')}
            options={genderOptions}
            id="gender"
            name="gender"
            value={formik.values.gender}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={!!formik.errors.gender && formik.touched.gender}
            helperText={formik.errors.gender}
          />
          <Select
            label={t('employee:MARRIAGE_STATUS')}
            placeholder={t('employee:MARRIAGE_STATUS')}
            options={marriageStatusOptions}
            value={formik.values.marriage_status}
            id="marriage_status"
            name="marriage_status"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={!!formik.errors.marriage_status && formik.touched.marriage_status}
            helperText={formik.errors.marriage_status}
          />
          <TextField
            type="number"
            inputProps={{
              min: 0,
              step: 1
            }}
            label={t('employee:NUMBER_OF_CHILDREN')}
            placeholder={t('employee:NUMBER_OF_CHILDREN')}
            id="children_count"
            name="children_count"
            value={formik.values.children_count}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={!!formik.errors.children_count && formik.touched.children_count}
            helperText={formik.errors.children_count}
          />
        </Box>
      )}
      {activeStep === 3 && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          height="100%"
        >
          {loading && <Loading />}
          {!loading && newEmployeeId && (
            <>
              <EmployeesGraphicIconSvg />
              <Typography
                variant="subtitle1"
                fontSize="1.5rem"
                color="heaven.main"
                marginTop="3rem"
              >
                {t('common:CONGRATULATION')}
              </Typography>
              <Typography variant="subtitle1" color="secondary">
                {t('employee:EMPLOYEE_CREATED')}
              </Typography>
              <Button
                type="button"
                className="mt-8"
                onClick={() => navigate(`/employees/${newEmployeeId}`)}
              >
                {t('common:SHOW_EMPLOYEE')}
              </Button>
              <Button
                type="button"
                variant="tertiary"
                className="mt-8"
                onClick={() => navigate(BACK_TO_OVERVIEW)}
              >
                {t('employee:TO_OVERVIEW')}
              </Button>
            </>
          )}
        </Box>
      )}
      <input type="submit" ref={btn} hidden />
    </Box>
  );
};
